import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { UserPlus, MoreHorizontal, Check, X, Shield, Building2, Crown } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface UserWithRoles {
  id: string;
  email: string | null;
  approved: boolean | null;
  created_at: string | null;
  organization_id: string | null;
  organizations: { name: string } | null;
  user_roles: { role: string }[];
}

const AVAILABLE_ROLES = ['admin', 'editor', 'moderator', 'user', 'super_admin', 'owner'] as const;

const AllUsers: React.FC = () => {
  const queryClient = useQueryClient();
  const { isOwner } = useOrganization();

  const { data: users, isLoading } = useQuery({
    queryKey: ['super-admin-all-users'],
    queryFn: async () => {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          approved,
          created_at,
          organization_id,
          organizations(name)
        `)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      const usersWithRoles = profiles.map((profile) => ({
        ...profile,
        user_roles: roles?.filter(r => r.user_id === profile.id).map(r => ({ role: r.role })) ?? []
      }));

      return usersWithRoles as UserWithRoles[];
    }
  });

  const { data: organizations } = useQuery({
    queryKey: ['super-admin-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteOrgId, setInviteOrgId] = useState("");
  const [makeSuperAdmin, setMakeSuperAdmin] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  // State for org change dialog
  const [orgChangeUser, setOrgChangeUser] = useState<UserWithRoles | null>(null);
  const [newOrgId, setNewOrgId] = useState("");

  const refetchUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['super-admin-all-users'] });
  };

  const handleInviteSuperAdmin = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    const orgId = inviteOrgId || organizations?.[0]?.id;
    if (!orgId) {
      toast.error("No organization available");
      return;
    }

    setInviteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: inviteEmail.trim().toLowerCase(),
          organization_id: orgId,
          role: 'admin',
          make_super_admin: makeSuperAdmin
        }
      });

      if (error) throw error;
      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(data?.message || `Invitation sent to ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail("");
      setMakeSuperAdmin(false);
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleApproveUser = async (userId: string, approve: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ approved: approve })
        .eq('id', userId);

      if (error) throw error;
      toast.success(approve ? "User approved" : "User approval revoked");
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to update user");
    }
  };

  const handleAddRole = async (userId: string, role: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });

      if (error) {
        if (error.code === '23505') {
          toast.error("User already has this role");
          return;
        }
        throw error;
      }
      toast.success(`Added ${role} role`);
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to add role");
    }
  };

  const handleRemoveRole = async (userId: string, role: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', role);

      if (error) throw error;
      toast.success(`Removed ${role} role`);
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to remove role");
    }
  };

  const handleChangeOrganization = async () => {
    if (!orgChangeUser || !newOrgId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ organization_id: newOrgId })
        .eq('id', orgChangeUser.id);

      if (error) throw error;
      toast.success("User organization updated");
      setOrgChangeUser(null);
      setNewOrgId("");
      refetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Failed to change organization");
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner': return 'destructive';
      case 'super_admin': return 'destructive';
      case 'admin': return 'default';
      case 'editor': return 'secondary';
      case 'moderator': return 'outline';
      case 'user': return 'outline';
      default: return 'outline';
    }
  };

  const getUserRoleNames = (user: UserWithRoles) => 
    user.user_roles?.map(r => r.role) ?? [];

  // Only owners can add/remove super_admin and owner roles
  const getAvailableRolesToAdd = (user: UserWithRoles) => {
    const existingRoles = getUserRoleNames(user);
    return AVAILABLE_ROLES.filter(role => {
      if (existingRoles.includes(role)) return false;
      // Only owners can grant super_admin or owner roles
      if ((role === 'super_admin' || role === 'owner') && !isOwner) return false;
      return true;
    });
  };

  const canRemoveRole = (role: string) => {
    // Only owners can remove super_admin or owner roles
    if ((role === 'super_admin' || role === 'owner') && !isOwner) return false;
    return true;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">All Users</h1>
          <p className="text-slate-500">Manage users across all organizations</p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Super Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Invite Super Admin</DialogTitle>
              <DialogDescription>
                Invite a new platform administrator with access to all organizations
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email Address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="admin@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={inviteLoading}
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <Label htmlFor="make-super">Grant Super Admin</Label>
                  <p className="text-xs text-muted-foreground">
                    Full platform access to all organizations
                  </p>
                </div>
                <Switch
                  id="make-super"
                  checked={makeSuperAdmin}
                  onCheckedChange={setMakeSuperAdmin}
                  disabled={inviteLoading}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteLoading}>
                Cancel
              </Button>
              <Button onClick={handleInviteSuperAdmin} disabled={inviteLoading}>
                {inviteLoading ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organization Change Dialog */}
      <Dialog open={!!orgChangeUser} onOpenChange={(open) => !open && setOrgChangeUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Organization</DialogTitle>
            <DialogDescription>
              Move {orgChangeUser?.email} to a different organization
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Organization</Label>
            <Select value={newOrgId} onValueChange={setNewOrgId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Choose organization..." />
              </SelectTrigger>
              <SelectContent>
                {organizations?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgChangeUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleChangeOrganization} disabled={!newOrgId}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800">Users</CardTitle>
          <CardDescription className="text-slate-500">{users?.length ?? 0} users registered</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-slate-500">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email ?? 'No email'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.organizations?.name ?? 'Unassigned'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.user_roles?.map((r, i) => (
                          <Badge key={i} variant={getRoleBadgeVariant(r.role)} className="flex items-center gap-1">
                            {r.role === 'owner' && <Crown className="h-3 w-3" />}
                            {r.role}
                          </Badge>
                        ))}
                        {(!user.user_roles || user.user_roles.length === 0) && (
                          <span className="text-muted-foreground text-sm">No roles</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.approved ? "default" : "outline"}>
                        {user.approved ? 'Approved' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {/* Approve/Revoke */}
                          {user.approved ? (
                            <DropdownMenuItem onClick={() => handleApproveUser(user.id, false)}>
                              <X className="h-4 w-4 mr-2" />
                              Revoke Approval
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleApproveUser(user.id, true)}>
                              <Check className="h-4 w-4 mr-2" />
                              Approve User
                            </DropdownMenuItem>
                          )}

                          <DropdownMenuSeparator />

                          {/* Change Organization */}
                          <DropdownMenuItem onClick={() => {
                            setOrgChangeUser(user);
                            setNewOrgId(user.organization_id || "");
                          }}>
                            <Building2 className="h-4 w-4 mr-2" />
                            Change Organization
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          {/* Add Roles */}
                          {getAvailableRolesToAdd(user).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                Add Role
                              </div>
                              {getAvailableRolesToAdd(user).map((role) => (
                                <DropdownMenuItem 
                                  key={`add-${role}`} 
                                  onClick={() => handleAddRole(user.id, role)}
                                >
                                  <Shield className="h-4 w-4 mr-2" />
                                  Add {role}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                            </>
                          )}

                          {/* Remove Roles */}
                          {getUserRoleNames(user).filter(role => canRemoveRole(role)).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                Remove Role
                              </div>
                              {getUserRoleNames(user).filter(role => canRemoveRole(role)).map((role) => (
                                <DropdownMenuItem 
                                  key={`remove-${role}`} 
                                  onClick={() => handleRemoveRole(user.id, role)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Remove {role}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AllUsers;
